import React, { useRef, useContext, useEffect, useState, useMemo } from 'react';
import { API } from 'aws-amplify';
import { t } from 'i18next';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAngleDown,
    faExclamationCircle,
    faArrowLeft,
    faCheck,
    faChevronCircleDown,
    faChevronCircleUp,
} from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';
import { Select, Typography, Box, MenuItem, Dialog, DialogContent } from '@mui/material';

import calcSchoolYear from '../../utils/calcSchoolYear';
import { ProtectedRouteContext, ShipyardContext } from '../Context';
import Button from '../shared/Button';
import LabeledInput from '../shared/LabeledInput';
import LabeledSelect from '../shared/LabeledSelect';
import DatePicker from '../shared/datePick/DatePick';
import Phone from '../shared/Phone';
import FileDrop from '../shared/FileDrop';
import Panel from '../shared/Panel';
import PanelSection from '../shared/PanelSection';
import View from '../shared/View';
import ViewHistory from '../shared/modals/ViewHistory';
import Status from '../shared/Status';
import ChangeApplicationStatus from './changeApplicationStatus';
import Form from '../shared/Form';
import DateTimeText from '../shared/DateTimeText';
import RecordChanges from './recordChanges';
import useRecordChanges from './recordChanges/useRecordChanges';
import ShipyardMenuButton from '../shared/shipyardMenu';
import ScholarshipData from './scholarshipData';
import TenDayTimelineEdit from './tenDayTimeline';
import RenderIf from '../renderIf';
import DeleteApplication from './deleteApplication';

import useRecord from './record/useRecord';

import './Record.scss';

const Markdown = ({ children }) => (
    <ReactMarkdown
        disallowedElements={['p']}
        unwrapDisallowed
        components={{
            em: ({ node, ...props }) => (
                <span
                    style={{ color: '#0F428C', fontStyle: 'italic', fontWeight: 550 }}
                    {...props}
                />
            ),
            strong: ({ node, ...props }) => (
                <span
                    style={{ color: '#0F428C', fontWeight: 550 }}
                    {...props}
                />
            ),
            code: ({ node, inline, ...props }) => (
                <span
                    style={{ color: 'green', fontWeight: 550 }}
                    {...props}
                />
            ),
        }}
        children={children}
    />
);

export default function Record(props) {
    const { displayModal, userSettings } = useContext(ProtectedRouteContext);
    const location = useLocation();
    const history = useHistory();
    const [initialized, setInitialized] = useState(false);
    const [key] = useState(location.pathname.slice(location.pathname.lastIndexOf('/') + 1));
    const [from] = useState(location.search.slice(1));
    const [record, setRecord] = useState(null);
    const [schoolYear] = useState(calcSchoolYear());
    const [doingAjax, setDoingAjax] = useState(true);
    const [actions, setActions] = useState([]);
    const [review, setReview] = useState(true);
    const [editable] = useState(from === 'admin' ? true : false);
    const [ajaxErrorMessage] = useState(t('General.AjaxError'));
    const [saving, setSaving] = useState(new Set());
    const [render, forceRender] = useState(Math.random());
    const [message, setMessage] = useState();
    const [messageExpanded, setMessageExpanded] = useState(true);
    const [changes, getRecordChanges, markResolved] = useRecordChanges(key);

    const [showDelete, setShowDelete] = useState(false);

    const {
        recordState,
        initializeRecord,
        handleDateChange,
        handleDateError,
        showErrors,
        handleChangeStatus,
    } = useRecord();

    const [scholarshipDataOpen, setScholarshipDataOpen] = useState(false);

    const appId = props.match.params.applicationId;
    const formRefs = useRef([]);
    const { setMoveConfig, resetMoveConfig } = useContext(ShipyardContext);

    let deleteFile = async (fileId, question) => {
        setDoingAjax(true);
        try {
            let newChosenVals = question.chosenOptions.filter(opt => opt !== fileId);
            await API.put('ShipyardAPI', `/application/${appId}/answer/${question.id}`, {
                body: { responseIds: newChosenVals },
            });

            await API.del('ShipyardAPI', `/application/docs/${fileId}`, {
                body: {
                    status: '0',
                },
            });

            setRecord(null);
        } catch (e) {
            toast.error(t('General.AjaxError'));
        } finally {
            setDoingAjax(false);
        }
    };

    useEffect(() => {
        if (record === null) {
            var retrieveRecord = async () => {
                try {
                    var [_record, _disabilities] = await Promise.all([
                        API.get('ShipyardAPI', `/application/${key}`, {
                            queryStringParameters: {
                                userType: userSettings.UserType,
                            },
                        }),
                        API.get('ShipyardAPI', '/application/disabilities'),
                    ]);

                    var [_statusChangeEligible, { Terms }] = await Promise.all([
                        API.get('ShipyardAPI', `/admin/action_check/status/${_record.appInstance.statusId}`),
                        API.get('ShipyardAPI', '/applications/terms'),
                        getRecordChanges(_record.appInstance.termId),
                    ]);

                    var disabilitiesList = [
                        {
                            id: -1,
                            text: {
                                'en-US': '',
                            },
                        },
                        ..._disabilities.map((disability) => {
                            return {
                                id: disability.OptionID,
                                text: {
                                    'en-US': disability.OptionText,
                                },
                            };
                        }),
                    ];

                    _record.docs = _record.docs.map((doc) => {
                        return {
                            docData: [doc],
                        };
                    });

                    var schoolNameQuestion = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 39)
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 39)
                        : null;

                    var gradeQuestion = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 36)
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 36)
                        : null;

                    var distIEPQuestion = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 38)
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 38)
                        : null;

                    var IEPorISPQuestion = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 102)
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 102)
                        : null;

                    var fullOrPartQuestion = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 75)
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 75)
                        : null;

                    var initYearQuestion = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 111)
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 111)
                        : null;

                    var locationQuestion = !!_record?.appDef?.sections[1]?.questions?.find((q) => q.id === 112)
                        ? _record.appDef.sections[1].questions.find((q) => q.id === 112)
                        : null;

                    var stateQuestion = !!_record?.appDef?.sections[1]?.questions?.find((q) => q.id === 50)
                        ? _record.appDef.sections[1].questions.find((q) => q.id === 50)
                        : null;

                    var studentFirstName = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 33)?.answerText
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 33).answerText
                        : '';
                    var studentFirstNameQuestion = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 33)
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 33)
                        : null;

                    var studentLastName = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 34)?.answerText
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 34).answerText
                        : '';
                    var studentLastNameQuestion = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 34)
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 34)
                        : null;

                    var parentFirstNameQuestion = !!_record?.appDef?.sections[1]?.questions?.find((q) => q.id === 44)
                        ? _record.appDef.sections[1].questions.find((q) => q.id === 44)
                        : null;

                    var parentLastNameQuestion = !!_record?.appDef?.sections[1]?.questions?.find((q) => q.id === 45)
                        ? _record.appDef.sections[1].questions.find((q) => q.id === 45)
                        : null;

                    var emailQuestion = !!_record?.appDef?.sections[1]?.questions?.find((q) => q.id === 45)
                        ? _record?.appDef?.sections[1].questions.find((q) => q.id === 46)
                        : null;

                    var phoneNumberQuestion = !!_record?.appDef?.sections[1]?.questions?.find((q) => q.id === 47)
                        ? _record.appDef.sections[1].questions.find((q) => q.id === 47)
                        : null;

                    var addressQuestion = !!_record?.appDef?.sections[1]?.questions?.find((q) => q.id === 48)
                        ? _record.appDef.sections[1].questions.find((q) => q.id === 48)
                        : null;

                    var cityQuestion = !!_record?.appDef?.sections[1]?.questions?.find((q) => q.id === 49)
                        ? _record.appDef.sections[1].questions.find((q) => q.id === 49)
                        : null;

                    var renewalQuestion1 = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 59)
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 59)
                        : null;

                    var renewalQuestion2 = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 60)
                        ? _record.appDef.sections[0].questions.find((q) => q.id === 60)
                        : null;

                    let eligibilityQuestion = null;
                    let enrollmentQuestion = null;
                    switch (_record?.appInstance?.applicationType) {
                        case 1:
                            if (!!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 40)) {
                                eligibilityQuestion = _record.appDef.sections[0].questions.find((q) => q.id === 40);
                            } else if (!!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 127)) {
                                eligibilityQuestion = _record.appDef.sections[0].questions.find((q) => q.id === 127);
                            }

                            enrollmentQuestion = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 41)
                                ? _record.appDef.sections[0].questions.find((q) => q.id === 41)
                                : null;
                            break;
                        case 2:
                            if (!!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 57)) {
                                eligibilityQuestion = _record.appDef.sections[0].questions.find((q) => q.id === 57);
                            } else if (!!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 131)) {
                                eligibilityQuestion = _record.appDef.sections[0].questions.find((q) => q.id === 131);
                            }

                            enrollmentQuestion = !!_record?.appDef?.sections[0]?.questions?.find((q) => q.id === 58)
                                ? _record.appDef.sections[0].questions.find((q) => q.id === 58)
                                : null;
                            break;
                        default:
                            eligibilityQuestion = null;
                            enrollmentQuestion = null;
                            break;
                    }

                    let generatedDocs = _record.docs.filter((e) => e?.docData?.[0]?.DocumentTypeID !== 4);

                    let _newRecord = {
                        name: `${studentFirstName} ${studentLastName}`,
                        uid: !!_record?.uid?.answerText ? _record.uid.answerText : 'No Unique ID',
                        stn: _record.stn.answerText,
                        appType: _record.appInstance.applicationType,
                        termDescription: Terms.find(term => term.value === _record?.appInstance?.termId)?.label,
                        statusRow: {
                            status: _record.appInstance.statusId,
                        },
                        appInfo:
                            _record?.appInfo !== null && typeof _record?.appInfo?.filter === 'function'
                                ? _record.appInfo.filter((info) => info.LanguageID === 1)
                                : null,
                        sectionA: {
                            studentFirstName: studentFirstNameQuestion,
                            studentLastName: studentLastNameQuestion,
                            dob: !!_record?.appDef?.sections[0].questions.find((q) => q.id === 35)
                                ? _record.appDef.sections[0].questions.find((q) => q.id === 35)
                                : null,
                            nextGrade: gradeQuestion,
                            districtIEP: distIEPQuestion,
                            school: schoolNameQuestion,
                            renewalQuestion1,
                            renewalQuestion2,
                        },
                        sectionB: {
                            parentFirstName: parentFirstNameQuestion,
                            parentLastName: parentLastNameQuestion,
                            email: emailQuestion,
                            phoneNumber: phoneNumberQuestion,
                            address: addressQuestion,
                            city: cityQuestion,
                            state: stateQuestion,
                            zip: _record?.appDef?.sections[1].questions.find((q) => q.id === 51),
                        },
                        sectionC: {
                            eligibility: eligibilityQuestion,
                        },
                        sectionD: {
                            enrollment: enrollmentQuestion,
                        },
                        docs:
                            userSettings.UserType >= 3
                                ? generatedDocs.concat(
                                    _record.appDef.sections[2].questions.filter((e) => e.questionType === 6)
                                )
                                : _record.appDef.sections[2].questions.filter((e) => e.questionType === 6 && e.documentType === 3),
                        osde: {
                            nationality: _record?.appDef?.sections[0].questions.find((q) => q.id === 42),
                            eds: _record?.appDef?.sections[0].questions.find((q) => q.id === 43),
                        },
                    };

                    if (userSettings.UserType >= 3) {
                        _newRecord = Object.assign({}, _newRecord, {
                            sectionA: {
                                ..._newRecord.sectionA,
                                primaryDisability:
                                    _record?.primDis?.chosenOptions && _record?.primDis?.chosenOptions[0] !== undefined
                                        ? _record.primDis.chosenOptions[0]
                                        : disabilitiesList[0].id,
                                secondaryDisability:
                                    _record?.secDis?.chosenOptions && _record?.secDis?.chosenOptions[0] !== undefined
                                        ? _record.secDis.chosenOptions[0]
                                        : disabilitiesList[0].id,
                                primaryDisabilityOptions: disabilitiesList,
                                secondaryDisabilityOptions: disabilitiesList,
                                acceptanceDate: !!_record?.appDef?.sections[0]?.questions.find((q) => q.id === 109)
                                    ? _record.appDef.sections[0].questions.find((q) => q.id === 109)
                                    : null,
                                revocationDate: !!_record?.appDef?.sections[0]?.questions.find((q) => q.id === 110)
                                    ? _record.appDef.sections[0].questions.find((q) => q.id === 110)
                                    : null,
                                initYear: initYearQuestion,
                                IEPorISP: IEPorISPQuestion,
                                fullOrPart: fullOrPartQuestion,
                            },
                            sectionB: {
                                ..._newRecord.sectionB,
                                payeeNum: _record?.appDef?.sections[1].questions.find((q) => q.id === 94),
                                tin: _record?.appDef?.sections[3].questions.find((q) => q.id === 52),
                                location: locationQuestion,
                            },
                        });
                    };

                    let _actions = [];

                    // TODO: This will be necessary once invoice creation and renewal are introduced in 1.1+
                    // const displayAcceptance = (toggle) => {
                    //     setDoingAjax(toggle);
                    //     let param2 = toggle ? () => (
                    //         <div className="accept-renewals-modal">
                    //             <div className="accept-renewals-modal-title">
                    //                 {t("Students.AcceptRenewals.ModalTitle")}
                    //             </div>
                    //             <div className="break accept-renewals-modal-break"/>
                    //             <div className="accept-renewals-sentence intro-sentence">
                    //                 {t("Students.AcceptRenewals.OnBehalfOf") + " " + _newRecord?.sectionA?.school + " " + t("Students.AcceptRenewals.WishTo")}
                    //             </div>
                    //             <div className="accept-renewals-modal-boxed-sentence">
                    //                 <span className="green-text">{t("Students.AcceptRenewals.ModalAccept")}</span>{" " + _newRecord?.name + " for the " + schoolYear + " " + t("Students.AcceptRenewals.AcceptMessage2")}
                    //             </div>
                    //             <div className="accept-renewals-sentence description-sentence">
                    //                 {t("Students.AcceptRenewals.AcceptMessage3")}
                    //             </div>
                    //             <div className="accept-renewals-modal-buttons">
                    //                 <Button
                    //                     type="grey"
                    //                     onClick={() => {displayAcceptance(false)}}
                    //                 >
                    //                     Cancel
                    //                 </Button>
                    //                 <Button
                    //                     type="primary"
                    //                     interactive={false}
                    //                 >
                    //                     Confirm
                    //                 </Button>
                    //             </div>
                    //         </div>
                    //     )
                    //     :
                    //         null;
                    //     displayModal(toggle, param2);
                    // };

                    const displayReview = (toggle) => {
                        handleChangeStatus(toggle);
                    };

                    const displayHistory = (toggle) => {
                        setDoingAjax(toggle);
                        let param2 = toggle
                            ? () => (
                                  <ViewHistory
                                      appId={appId}
                                      onClose={() => {
                                          displayHistory(false);
                                      }}
                                  />
                              )
                            : null;

                        displayModal(toggle, param2, toggle);
                    };

                    // TODO: This will be necessary once renewal is introduced in 1.1+
                    // const renewActionJSX = <li key="action-renew">
                    //     <div
                    //         onClick={() => {
                    //             displayAcceptance(true);
                    //         }}
                    //     >
                    //         {t("Record.RenewApplicationAction")}
                    //     </div>
                    // </li>;

                    const reviewAction = {
                        label: t('Record.ReviewApplicationAction'),
                        action: () => displayReview(true),
                    };

                    const enrollmentChangeAction = {
                        label: 'Change enrollment',
                        action: () => history.push(location.pathname + '/enrollment_change'),
                    };

                    const historyAction = {
                        label: t('Record.ViewHistoryAction'),
                        action: () => displayHistory(true),
                    };

                    // TODO: This will be necessary once invoice creation and renewal are introduced in 1.1+
                    // switch(_newRecord?.statusRow?.status) {
                    //     case 6:
                    //         switch(userSettings.UserType) {
                    //             case 2:
                    //                 setActions([reviewActionJSX, renewActionJSX, invoiceActionJSX, historyActionJSX]);
                    //                 break;
                    //             default:
                    //                 setActions([reviewActionJSX, historyActionJSX]);
                    //                 break;
                    //         }
                    //         break;
                    //     default:
                    //         setActions([reviewActionJSX, historyActionJSX]);
                    //         break;
                    // };

                    if (_statusChangeEligible.canChange) _actions.push(reviewAction);

                    _actions.push(historyAction);
                    const getMoveOptions = (isFirstPage) => ({
                        applicationInstanceId: key,
                        action: 'ownership change',
                        dataKey: 'UserID',
                        label1: t('MoveRecord.Ownership.SelectedApplicationTitle'),
                        label2: t('MoveRecord.Ownership.SearchForOwnerTitle'),
                        description1: t('MoveRecord.Ownership.SelectedApplicationDescription'),
                        description2: <Markdown>{t('MoveRecord.Ownership.SearchForOwnerDescription')}</Markdown>,
                        moveViewTitle: t('MoveRecord.Ownership.ViewTitle'),
                        inputPlaceholder: t('MoveRecord.Application.SearchPlaceholder'),
                        possibleMatchesLanguage: '',
                        buttonText: 'Change Ownership',
                        sort: '`User First Name` ASC',
                        modalTitle: isFirstPage ? 'Change application ownership?' : 'Do you want to complete the ownership change?',
                        overrideWarning: <Box sx={{ marginBottom: (theme) => theme.spacing(1) }} />,
                        getURL: (id) => `/admin/application_instance/${id}/ownership_change_candidates`,
                        updateURL: (id) => `/admin/application_instance/${id}/ownership_change`,
                        confirmText: isFirstPage ? 'Next' : 'Yes, Change Ownership',
                        cancelText: isFirstPage ? 'Cancel Change' : 'No, Cancel Change',
                        nextHandlerOverride: isFirstPage
                            ? async (targetId, ajaxSetter, questions) => {
                                  if (typeof ajaxSetter === 'function') ajaxSetter(true);
                                  let id = toast.loading('Confirming move options...');
                                  try {
                                      let { confirmationData } = await API.post(
                                          'ShipyardAPI',
                                          `/admin/application_instance/${key}/ownership_change_confirmation`,
                                          {
                                              body: {
                                                  toUserId: targetId,
                                                  answers: questions.map((question) => {
                                                      return {
                                                          id: question.id,
                                                          value: !!question.value ? true : false,
                                                      };
                                                  }),
                                              },
                                          }
                                      );

                                      if (typeof ajaxSetter === 'function') ajaxSetter(false);
                                      toast.dismiss(id);
                                      toast.success('Move configuration confirmed.');

                                      return setMoveConfig({
                                          ...getMoveOptions(false),
                                          buildComparison: () => {
                                              return (
                                                  <ul>
                                                      {confirmationData.map((data, i) => {
                                                          return (
                                                              <Typography
                                                                  component="li"
                                                                  key={`confirm-${i}`}
                                                              >
                                                                  {data}
                                                              </Typography>
                                                          );
                                                      })}
                                                  </ul>
                                              );
                                          },
                                          buildConfirmDescription: (target, toRecord, modalContentOverride) => {
                                              return (
                                                  <>
                                                      <Typography
                                                          sx={{ color: (theme) => theme.palette.error.main, fontSize: 20, fontWeight: 700 }}
                                                      >
                                                          WARNING:
                                                      </Typography>
                                                      <Typography sx={modalContentOverride}>
                                                          {`The action you are about to take is final and cannot be undone. Are you sure you want to change the user ownership for ${target.FirstName} ${target.LastName}'s (ID ${target.UniqueID}) ${target.TermDescription} application to the selected user ${toRecord['User First Name']} ${toRecord['User Last Name']} (User ID ${toRecord.Username})?   `}
                                                      </Typography>
                                                  </>
                                              );
                                          },
                                      });
                                  } catch (e) {
                                      toast.dismiss(id);
                                      toast.error(ajaxErrorMessage);
                                      if (typeof ajaxSetter === 'function') ajaxSetter(true);
                                  }
                              }
                            : null,
                        cancelHandlerOverride: () => {
                            return setMoveConfig(getMoveOptions(true));
                        },
                        buildBody: (target, toRecord, modalQuestions) => {
                            return {
                                toUserId: toRecord.UserID,
                                answers: modalQuestions.map((question) => {
                                    return {
                                        id: question.id,
                                        value: question.value,
                                    };
                                }),
                            };
                        },
                        buildComparison: () => {
                            return null;
                        },
                        buildConfirmDescription: (target, toRecord, modalContentOverride) => {
                            return (
                                <>
                                    <Typography sx={{ fontSize: 18, color: '#1F4E92', fontWeight: 700 }}>Clear Application Data</Typography>
                                    <Typography sx={modalContentOverride}>
                                        If you need to clear any data from the application prior to the new user accessing the application,
                                        you may do so now. You can clear all the data in a section of the application by selecting 'Yes' for
                                        that section. If you do not wish to clear any data, leave all sections as 'No' and click 'Next'.
                                    </Typography>
                                </>
                            );
                        },
                        shouldRenderModalContent: isFirstPage,
                        renderModalContent: isFirstPage
                            ? (modalQuestions) => {
                                  return (
                                      <>
                                          <Box sx={{ marginTop: '16px' }}>
                                              {modalQuestions.map((question) => (
                                                  <Box sx={{ marginTop: '24px' }}>
                                                      <Typography sx={{ marginBottom: '8px' }}>{t(question.label)}</Typography>
                                                      <Select
                                                          value={Number.isInteger(question.value) ? question.value : ''}
                                                          onChange={(e) => question?.onChange(e.target.value)}
                                                          sx={{ width: '240px' }}
                                                      >
                                                          {question.options.map((option) => (
                                                              <MenuItem value={option.value}>{option.label}</MenuItem>
                                                          ))}
                                                      </Select>
                                                  </Box>
                                              ))}
                                          </Box>
                                      </>
                                  );
                              }
                            : () => null,
                    });

                    if (userSettings.UserType > 2) {
                        _actions.push({
                            label: t('Record.MoveApplicationOwnership'),
                            action: () => {
                                setMoveConfig(getMoveOptions(true));
                                history.push('/actions/move-record');
                            },
                        });
                    }

                    if (userSettings.UserType >= 3 && _record.appInstance.statusId === 9) {
                        _actions.push({
                            label: 'View scholarship data',
                            action: () => setScholarshipDataOpen(true),
                        });
                    }

                    if (userSettings.UserType > 2) _actions.push(enrollmentChangeAction);

                    if(userSettings?.UserType >= 3) {
                        _actions.push({
                            label: 'Delete Application',
                            action: () => setShowDelete(true),
                            type: 'error',
                        });
                    }

                    setActions(_actions);

                    setMessage(!!_record?.appInstance?.message ? _record?.appInstance?.message : undefined);

                    initializeRecord?.({
                        termId: _record?.appInstance?.termId || -1,
                        ..._newRecord
                    });
                    setRecord(_newRecord);
                    if (userSettings) setDoingAjax(false);
                } catch (e) {
                    toast.error(ajaxErrorMessage);
                }
            };

            retrieveRecord();
        }
    }, [record, key, userSettings, displayModal, schoolYear, appId, ajaxErrorMessage, history, location.pathname]);

    useEffect(() => {
        if (userSettings && !initialized) {
            resetMoveConfig();
            if (record) setDoingAjax(false);
            setInitialized(true);
        }
    }, [record, userSettings, initialized]);

    const messageJSX = useMemo(() => {
        return !!message ? (
            <div
                key={`app-${message.AppInstID}`}
                className="review-message"
            >
                <div>
                    <FontAwesomeIcon icon={faExclamationCircle} /> {t('Record.NotificationMessage')}{' '}
                    <FontAwesomeIcon
                        className="collapse-button"
                        icon={messageExpanded ? faChevronCircleDown : faChevronCircleUp}
                        onClick={() => setMessageExpanded((prev) => !prev)}
                    />
                </div>
                {messageExpanded ? (
                    <>
                        <hr />
                        <div className="change-reason-container">
                            <div className="message-container">{message.ChangeReason}</div>
                            <div className="signature-container">
                                {message.LastUpdatedByName}
                                <DateTimeText
                                    type="long-time"
                                    date={message.LastUpdatedUTC}
                                />
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        ) : null;
    }, [message, messageExpanded]);

    const handleBack = () => {
        if (from === 'renewals') {
            history.push('/students/renewals');
        } else if (from === 'admin') {
            history.push('/applications');
        } else {
            history.push('/students', location.state);
        }
    };

    const handleSave = (newFormState) => {
        setDoingAjax(true);
        showErrors?.();
        let _blankDate = 'MM/DD/YYYY';
        let prev = record;
        let _keys = Object.keys(newFormState);
        let _values = Object.values(newFormState);

        prev.name = `${newFormState['sectionA.studentFirstName']} ${newFormState['sectionA.studentLastName']}`;
        prev.statusRow.submittedBy = `${newFormState['sectionB.parentFirstName']} ${newFormState['sectionB.parentLastName']}`;

        for (let i = 0; i < _keys.length; i++) {
            let key1 = _keys[i].split('.')[0],
                key2 = _keys[i].split('.')[1];
            try {
                switch (key1) {
                    case 'stn':
                        prev[key1] = _values[i];
                        break;
                    default:
                        switch (prev[key1][key2].questionType) {
                            case 1:
                                prev[key1][key2].answerText = _values[i];
                                break;
                            case 5:
                                prev[key1][key2].answerText = !!_values[i] && _values[i] !== _blankDate ? moment.tz(`${_values[i]} 12:00:00`, userSettings.ClientTimeZone).toISOString() : '';
                                break;
                            case undefined:
                                prev[key1][key2] = parseInt(_values[i]);
                                break;
                            default:
                                prev[key1][key2].chosenOptions = [_values[i]];
                                break;
                        }
                        break;
                }
            } catch (e) {
                toast.error(ajaxErrorMessage);
            }
        }

        setRecord({...prev});
    };

    const handleOption = (section, key, value) => {
        value = parseInt(value);
        if (isNaN(value)) {
            return;
        }
        setRecord((prev) => {
            switch (section) {
                case 'osde':
                case 'sectionA':
                case 'sectionC':
                case 'sectionD':
                    prev[section][key].chosenOptions[0] = value;
                    break;
                default:
                    prev[section][key] = value;
                    prev[section][`${key}Question`].chosenOptions = [value];
            }
            return prev;
        });
    };

    const handleCheckbox = (e) => {
        let _checked = new Set(record.osde.nationality.chosenOptions);
        if (e?.target?.checked === true) _checked.add(parseInt(e.target.value));
        else _checked.delete(parseInt(e.target.value));

        setRecord((prev) => {
            prev.osde.nationality.chosenOptions = Array.from(_checked);
            return prev;
        });
    };

    const handleValidSave = async () => {
        setDoingAjax(true);

        let _applicant = {
            stn: record.stn,
            firstName: record.sectionA.studentFirstName.answerText,
            lastName: record.sectionA.studentLastName.answerText,
            dob: record.sectionA.dob.answerText,
            grade: record.sectionA.nextGrade.chosenOptions[0],
            primaryDisability: record.sectionA.primaryDisability,
            secondaryDisability: record.sectionA.secondaryDisability,
            iepSD: record.sectionA.districtIEP.chosenOptions[0],
            school: record.sectionA.school.chosenOptions[0],
            initYear: record.sectionA.initYear.chosenOptions[0],
            revocationDate: record.sectionA.revocationDate.answerText,
            acceptanceDate: record.sectionA.acceptanceDate.answerText,
            IEPorISP: record.sectionA.IEPorISP.chosenOptions[0],
        };

        let _parent = {
            firstName: record.sectionB.parentFirstName.answerText,
            lastName: record.sectionB.parentLastName.answerText,
            email: record.sectionB.email.answerText,
            phone: record.sectionB.phoneNumber.answerText,
            addr1: record.sectionB.address.answerText,
            city: record.sectionB.city.answerText,
            state: record.sectionB.state.chosenOptions[0],
            zip: record.sectionB.zip.answerText,
            payeeNum: record.sectionB.payeeNum.answerText,
            location: record.sectionB.location.chosenOptions[0],
            ssn: record.sectionB.tin.answerText,
        };

        let _freeform = [],
            _entry;

        let _parse = Object.values(record.osde);

        for (let i = 0; i < _parse.length; i++) {
            switch(_parse[i].questionType) {
                case 3://Radio
                    _entry = [{ questionId: _parse[i].id }, { responseIDs: [_parse[i].chosenOptions?.[0]] }, { responseText: _parse[i].answerText }];
                    break;
                case 4://Checkbox
                    _entry = [{ questionId: _parse[i].id }, { responseIDs: _parse[i].chosenOptions }, { responseText: _parse[i].answerText }];
                    break;
                default: 
                    console.error('questionType not implemented.');
            }

            _freeform.push(_entry);
        }

        _parse = record.sectionC.eligibility;

        _entry = [{ questionId: _parse.id }, { responseIDs: [_parse.chosenOptions?.[0]] }, { responseText: _parse.answerText }];

        _freeform.push(_entry);

        _parse = record.sectionD.enrollment;

        if (_parse !== null) {
            _entry = [{ questionId: _parse.id }, { responseIDs:[ _parse.chosenOptions?.[0]] }, { responseText: _parse.answerText }];

            _freeform.push(_entry);
        }

        _parse = record.sectionA.fullOrPart;

        _entry = [{ questionId: _parse.id }, { responseIDs: record.sectionA.fullOrPart.chosenOptions }, { responseText: '' }];

        _freeform.push(_entry);

        if (record.appType === 2) {
            _parse = record.sectionA.renewalQuestion1;

            _entry = [{ questionId: _parse.id }, { responseIDs: record.sectionA.renewalQuestion1.chosenOptions }, { responseText: '' }];

            _freeform.push(_entry);

            _parse = record.sectionA.renewalQuestion2;

            _entry = [{ questionId: _parse.id }, { responseIDs: null }, { responseText: _parse.answerText }];

            _freeform.push(_entry);
        }

        try {
            await API.patch('ShipyardAPI', `/admin/application_instance/${appId}`, {
                body: {
                    applicant: _applicant,
                    parent: _parent,
                    freeform: _freeform,
                },
            });

            setDoingAjax(false);

            toast.success(t('General.ChangesSaved'));
        } catch (e) {
            setDoingAjax(false);
            toast.error(ajaxErrorMessage);
        }
    };

    const shimmerJSX = useMemo(() => {
        return <div className="placeholder shimmer" />;
    }, []);

    const recordTitleJSX = (
        <h2 className="record-view-title">
            {record?.name ? `${record.name}` : 'Student Record'}
            {record?.uid ? ` (${record.uid})` : ' (No ID)'}
        </h2>
    );

    const blueCheckJSX = useMemo(() => {
        return <FontAwesomeIcon icon={faCheck} />;
    }, []);

    const recordDisplayCheck = (el) => {
        return typeof el === 'object' && el !== null;
    };

    const addToRefs = (el) => {
        if (el && !formRefs.current.includes(el)) {
            formRefs.current.push(el);
        }
    };

    function inputJSXGenerator() {
        let _formKey, _options, _question;

        function parseValidators(regex) {
            let _regex = RegExp(regex.rgx);
            let _failText = regex.errorText;
            return {
                rgx: _regex,
                failText: _failText['en-US'],
            };
        }

        switch (arguments.length) {
            case 2:
                switch (arguments[1]) {
                    case 'stn':
                        return (
                            <LabeledInput
                                validators={[
                                    {
                                        rgx: /^\d*$/,
                                        failText: t('Record.STNWarning'),
                                    },
                                ]}
                                labelText={arguments[0]}
                                formKey={arguments[1]}
                                ref={addToRefs}
                                id={arguments[1]}
                                value={record[arguments[1]]}
                                className={arguments[1]}
                                disabled={!editable || doingAjax}
                                autoComplete="nope"
                            />
                        );
                    default:
                        return (
                            <LabeledInput
                                formKey={arguments[1]}
                                ref={addToRefs}
                                id={arguments[1]}
                                value={record[arguments[1]]}
                                className={arguments[1]}
                                disabled={!editable || doingAjax}
                                autoComplete="nope"
                            />
                        );
                }
            case 3:
                _formKey = `${arguments[1]}.${arguments[2]}`;
                switch (arguments[2]) {
                    case 'nextGrade':
                    case 'districtIEP':
                    case 'school':
                    case 'state':
                    case 'fullOrPart':
                    case 'initYear':
                    case 'IEPorISP':
                    case 'location':
                        _question = record[arguments[1]][arguments[2]];

                        _options = _question.options;

                        _options = [
                            {
                                id: -1,
                                text: {
                                    'en-US': '',
                                },
                            },
                            ..._options,
                        ];

                        return (
                            <LabeledSelect
                                labelText={arguments[0]}
                                formKey={_formKey}
                                ref={addToRefs}
                                id={arguments[2]}
                                disabled={!editable}
                                value={_question.chosenOptions.length > 0 ? _question.chosenOptions[0] : -1}
                                required={_question.minResponseRequired === 1 && userSettings.UserType >= 3 ? true : false}
                            >
                                {_options.map((opt) => {
                                    return (
                                        <option
                                            id={opt.id}
                                            key={`${arguments[1]}-option-${opt.id}`}
                                            value={opt.id}
                                            disabled={opt?.isDisplayOnly === 1}
                                        >
                                            {opt.text['en-US']}
                                        </option>
                                    );
                                })}
                            </LabeledSelect>
                        );
                    case 'renewalQuestion1':
                        return (
                            <div>
                                <ReactMarkdown
                                    components={{
                                        p: ({ node, ...props }) => (
                                            <>
                                                <Box
                                                    component="span"
                                                    sx={{ fontSize: '1.2rem', fontWeight: 500, color: (theme) => theme.palette.error.main }}
                                                >
                                                    &#8270;
                                                </Box>{' '}
                                                <span
                                                    {...props}
                                                    style={{ fontWeight: 500, fontSize: '0.85rem' }}
                                                />
                                            </>
                                        ),
                                    }}
                                >
                                    {record.sectionA.renewalQuestion1.text['en-US']}
                                </ReactMarkdown>
                                <div>
                                    {record.sectionA.renewalQuestion1.options.map((_opt) => {
                                        return (
                                            <div
                                                className="osde-options-container"
                                                key={`renewalQuestion1-option-${_opt.id}`}
                                            >
                                                <label className="options">
                                                    <input
                                                        type="radio"
                                                        id={`renewalQuestion1-${_opt.id}`}
                                                        defaultChecked={_opt.id === record.sectionA.renewalQuestion1.chosenOptions[0]}
                                                        name="renewalQuestion1"
                                                        value={_opt.id}
                                                        onClick={() => {
                                                            handleOption('sectionA', 'renewalQuestion1', _opt.id);
                                                        }}
                                                        disabled={!editable}
                                                    />
                                                    <ReactMarkdown className="question-option">{_opt.text['en-US']}</ReactMarkdown>
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    case 'renewalQuestion2':
                        _question = record[arguments[1]][arguments[2]];

                        return (
                            <div>
                                <LabeledInput
                                    labelText={record.sectionA.renewalQuestion2.text['en-US']}
                                    validators={_question.responseRegexes.map(parseValidators)}
                                    formKey={_formKey}
                                    ref={addToRefs}
                                    id={arguments[2]}
                                    value={_question.answerText}
                                    disabled={!editable || doingAjax}
                                    required={_question.minResponseRequired === 1 && userSettings.UserType >= 3 ? true : false}
                                    autoComplete="nope"
                                />
                            </div>
                        );
                    case 'primaryDisability':
                    case 'secondaryDisability':
                        _options = record[arguments[1]][`${arguments[2]}Options`];
                        return (
                            <LabeledSelect
                                labelText={arguments[0]}
                                formKey={_formKey}
                                ref={addToRefs}
                                id={arguments[1]}
                                disabled={!editable}
                                value={record[arguments[1]][arguments[2]]}
                            >
                                {_options.map((opt) => {
                                    return (
                                        <option
                                            id={opt.id}
                                            key={`${arguments[1]}-option-${opt.id}`}
                                            disabled={opt?.isDisplayOnly === 1}
                                            value={opt.id}
                                        >
                                            {opt.text['en-US']}
                                        </option>
                                    );
                                })}
                            </LabeledSelect>
                        );
                    case 'dob':
                    case 'acceptanceDate':
                    case 'revocationDate':
                        _question = recordState[arguments[1]][arguments[2]];

                        const RequiredMarker = () => {
                            return (<Box
                                component="span"
                                sx={theme => {
                                    return {
                                        color: theme.palette.error.main,
                                        fontSize: "1.2rem"
                                    };
                                }}
                            >
                                &#8270;
                            </Box>);
                        }

                        let _dateValue = !!_question?.answerText && _question.answerText !== '0000-00-00 00:00:00' && _question.answerText !== ''
                        ? moment.tz(_question.answerText, userSettings.ClientTimeZone)
                        : moment.tz(null, userSettings.ClientTimeZone);

                        return (
                            <Box
                                sx={(theme) => {
                                    return {
                                        padding: '0.25rem',
                                    };
                                }}
                            >
                                <Typography
                                    sx={(theme) => {
                                        return {
                                            fontSize: '0.85rem',
                                            fontWeight: 500,
                                            color: theme.palette.grey.header,
                                        };
                                    }}
                                >
                                    {_question.minResponseRequired === 1 && userSettings.UserType >= 3 ? <RequiredMarker /> : null}{' '}
                                    {arguments[0]}
                                </Typography>
                                <DatePicker
                                    value={_dateValue}
                                    disabled={!editable || doingAjax}
                                    validated={true}
                                    size="small"
                                    inputRef={addToRefs}
                                    clearable
                                    width="19rem"
                                    inputProps={{
                                        'data-form-key': _formKey,
                                    }}
                                    maxDate={arguments[2] === 'dob' ? moment.tz(new Date(), userSettings.ClientTimeZone) : undefined}
                                    onChange={(newVal) => {
                                        handleDateChange?.(arguments[1], arguments[2], newVal);
                                    }}
                                    onError={(err, val) => {
                                        handleDateError?.(arguments[1], arguments[2], err, val);
                                    }}
                                    error={recordState?.shouldShowErrors && recordState?.dateErrors?.find((obj) => obj.id === _formKey)}
                                    textFieldSx={{
                                        '& .MuiOutlinedInput-root': {
                                            fontSize: '0.85rem',
                                        },
                                        '.Mui-disabled': {
                                            WebkitTextFillColor: 'unset',
                                            fontWeight: 700,
                                            backgroundColor: '#f4f4f4',
                                            color: (theme) => theme.palette.grey['field-text'],
                                            opacity: 1,
                                            border: '1px solid #f4f4f4',
                                        },
                                    }}
                                />
                                <RenderIf
                                    isTrue={recordState?.shouldShowErrors && recordState?.dateErrors?.find((obj) => obj.id === _formKey)}
                                >
                                    <Typography
                                        color="error"
                                        sx={(theme) => {
                                            return {
                                                fontSize: '0.8rem',
                                            };
                                        }}
                                    >
                                        {recordState?.dateErrors?.find((obj) => obj.id === _formKey)?.message || '(Required)'}
                                    </Typography>
                                </RenderIf>
                            </Box>
                        );
                    case 'payeeName':
                        return (
                            <LabeledInput
                                labelText={arguments[0]}
                                id={arguments[2]}
                                value={`${record?.sectionB?.parentFirstName?.answerText} ${record?.sectionB?.parentLastName?.answerText}`}
                                disabled={true}
                            />
                        );
                    case 'tin':
                        _question = record[arguments[1]][arguments[2]];

                        return (
                            <LabeledInput
                                masked={true}
                                disabled={!editable}
                                validators={_question.responseRegexes.map(parseValidators)}
                                labelText={arguments[0]}
                                formKey={_formKey}
                                ref={addToRefs}
                                id={arguments[2]}
                                value={_question.answerText}
                                required={_question.minResponseRequired === 1 && userSettings.UserType >= 3 ? true : false}
                                autoComplete="nope"
                            />
                        );
                    case 'phoneNumber':
                        _question = record[arguments[1]][arguments[2]];

                        return (
                            <Phone
                                disabled={!editable}
                                labelText={arguments[0]}
                                formKey={_formKey}
                                ref={addToRefs}
                                id={arguments[2]}
                                value={_question.answerText}
                                required={_question.minResponseRequired > 0}
                            />
                        );
                    default:
                        _question = record[arguments[1]][arguments[2]];

                        return (
                            <LabeledInput
                                labelText={arguments[0]}
                                validators={_question.responseRegexes.map(parseValidators)}
                                formKey={_formKey}
                                ref={addToRefs}
                                id={arguments[2]}
                                value={_question.answerText}
                                disabled={!editable || doingAjax}
                                required={_question.minResponseRequired === 1 && userSettings.UserType >= 3 ? true : false}
                                autoComplete="nope"
                            />
                        );
                }
            default:
                console.error(`Invalid amount of arguments: ${arguments.length}`, arguments);
        }
    }

    function documentsJSXGenerator() {
        switch (arguments.length) {
            case 0:
                if (record) {
                    let _jsxParents = record.docs.map((doc, i) => {
                        let handleDrop = async (event, question) => {
                            event?.preventDefault();
                            event?.stopPropagation();

                            let _fileDropped = event?.dataTransfer?.files || event?.target?.files;

                            let _validFileRegex = question.responseRegexes.map((regex) => new RegExp(regex.rgx));

                            if (_fileDropped && _validFileRegex.every((rgx) => rgx.test(_fileDropped[0].type))) {
                                setSaving((prev) => {
                                    prev.add(question.id);
                                    return new Set(prev);
                                });

                                let s3Call;
                                try {
                                    s3Call = await API.get('ShipyardAPI', `/application/${appId}/docs/${question.id}/target`, {
                                        queryStringParameters: {
                                            fileName: _fileDropped[0].name,
                                            documentType: question.documentType,
                                            mimeType: _fileDropped[0].type,
                                        },
                                    });
                                } catch (e) {
                                    toast.error(t('General.AjaxError'));

                                    setSaving((prev) => {
                                        prev.delete(question.id);
                                        return new Set(prev);
                                    });

                                    return;
                                }

                                //fetch() call here to the URL provided by the above API call
                                let fetchCall;
                                try {
                                    let options = {
                                        body: _fileDropped[0],
                                        headers: {
                                            'Content-Type': '',
                                        },
                                        method: 'PUT',
                                    };

                                    fetchCall = await fetch(s3Call.url, options);

                                    if (!fetchCall.ok) {
                                        toast.error(t('General.AjaxError'));

                                        setSaving((prev) => {
                                            prev.delete(question.id);
                                            return new Set(prev);
                                        });

                                        return;
                                    }
                                } catch (e) {
                                    toast.error(t('General.AjaxError'));

                                    setSaving((prev) => {
                                        prev.delete(question.id);
                                        return new Set(prev);
                                    });

                                    return;
                                }

                                try {
                                    await API.put('ShipyardAPI', `/application/docs/${s3Call.fileId}`, {
                                        body: {
                                            status: '1', //Uploaded
                                        },
                                    });
                                } catch (e) {
                                    toast.error(t('General.AjaxError'));

                                    setSaving((prev) => {
                                        prev.delete(question.id);
                                        return new Set(prev);
                                    });

                                    return;
                                }

                                try {
                                    var newChosenVals = question?.maxResponseRequired === 1 ? [s3Call.fileId] : [...question.chosenOptions, s3Call.fileId];
                                    await API.put('ShipyardAPI', `/application/${appId}/answer/${question.id}`, {
                                        body: { responseIds: newChosenVals },
                                    });
                                } catch (e) {
                                    toast.error(t('General.AjaxError'));

                                    setSaving((prev) => {
                                        prev.delete(question.id);
                                        return new Set(prev);
                                    });

                                    return;
                                }

                                let _file = {
                                    FileName: _fileDropped[0].name,
                                    FileID: s3Call.fileId,
                                    LastUpdatedUTC: new Date().toISOString(),
                                };

                                setRecord((prev) => {
                                    prev.docs = prev.docs.map((doc) => {
                                        let _doc = { ...doc };
                                        if (_doc.id === question.id && (question.maxResponseRequired === 1 || typeof question?.maxResponseRequired === 'undefined')) {
                                            _doc.docData[0] = _file;
                                            _doc.chosenOptions = newChosenVals;
                                        } else if (doc.id === question.id && question.maxResponseRequired > 1) {
                                            _doc.docData = [...question.docData, _file];
                                            _doc.chosenOptions = newChosenVals;
                                        }

                                        return _doc;
                                    });

                                    return prev;
                                });

                                forceRender(Math.random());

                                setSaving((prev) => {
                                    prev.delete(question.id);
                                    return new Set(prev);
                                });
                            } else {
                                toast.error(t('File.IncorrectType'));
                            }
                        };

                        let handleDrag = (event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        };

                        const handleRemove = (id) => {
                            let question = record.docs.find((file) => file?.docData?.find?.(doc => doc.FileID === id));
                            let param2 = () => (
                                <Dialog open={true}>
                                    <DialogContent className="deletion-warning-container">
                                        <Box>
                                            <div className="deletion-warning-text">
                                                <label>
                                                    Are you sure you want to delete the {question.text[userSettings.LanguagePreference]}:
                                                </label>
                                                <br />
                                                {question?.docData?.find?.(doc => doc.FileID === id).FileName}
                                            </div>
                                        </Box>
                                        <Box>
                                            <Button
                                                useMui={true}
                                                color="error"
                                                onClick={() => {
                                                    displayModal(false, null);
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                useMui={true}
                                                onClick={() => {
                                                    displayModal(false, null);
                                                    deleteFile(id, question);
                                                }}
                                            >
                                                Continue
                                            </Button>
                                        </Box>
                                    </DialogContent>
                                </Dialog>
                            );
                            displayModal(true, param2);
                        };

                        let _files;
                        if (doc.docData[0] !== undefined) {
                            _files = [
                                {
                                    name: doc.docData[0].FileName,
                                    fileId: doc.docData[0].FileID,
                                    s3id: doc.docData[0].S3ID,
                                    uploadedOn: doc.docData[0].LastUpdatedUTC,
                                    type: 'pdf',
                                },
                            ];
                        } else {
                            _files = [];
                        }

                        if(doc?.maxResponseRequired > 1) {
                            return [
                                ...doc.docData.map((dd, j) =>
                                    <div key={`doc-key-${i}-${j+1}`}>
                                        <FileDrop
                                            labelText={
                                                !!doc.text
                                                    ? doc.text[userSettings.LanguagePreference]
                                                    : typeof dd.DocumentType === 'string'
                                                    ? dd.DocumentType
                                                    : 'Miscellaneous or Generated Document'
                                            }
                                            classes={['parent-record-doc']}
                                            uploading={saving.has(doc.id)}
                                            disabled={doingAjax}
                                            downloadOnly={Number.isInteger(doc?.id) ? false : true}
                                            key={`file-drop-${
                                                dd !== undefined ? dd.FileID : Math.floor(Math.random() * 100)
                                            }`}
                                            files={[
                                                {
                                                    name: dd.FileName,
                                                    fileId: dd.FileID,
                                                    s3id: dd.S3ID,
                                                    uploadedOn: dd.LastUpdatedUTC,
                                                    type: 'pdf',
                                                }
                                            ]}
                                            drop={(event) => handleDrop(event, doc)}
                                            required={doc?.minResponseRequired > 0 ? true : false}
                                            dragover={handleDrag}
                                            remove={handleRemove}
                                        />
                                    </div>
                                ),
                                <div key={`doc-key-${i}-0`}>
                                    <RenderIf isTrue={doc.docData.length < doc.maxResponseRequired}>
                                        <FileDrop
                                            labelText={
                                                !!doc.text
                                                    ? doc.text[userSettings.LanguagePreference]
                                                    : typeof doc.docData[0]?.DocumentType === 'string'
                                                    ? doc.docData[0].DocumentType
                                                    : 'Miscellaneous or Generated Document'
                                            }
                                            classes={['parent-record-doc']}
                                            uploading={saving.has(doc.id)}
                                            disabled={doingAjax}
                                            downloadOnly={Number.isInteger(doc?.id) ? false : true}
                                            key={`file-drop-${Math.floor(Math.random() * 100)}`}
                                            files={[]}
                                            drop={(event) => handleDrop(event, doc)}
                                            required={doc?.minResponseRequired > 0 ? true : false}
                                            dragover={handleDrag}
                                            remove={handleRemove}
                                        />
                                        <span>{t('File.AcceptedTypes', { acceptedFileTypes: doc.acceptedFileTypes })}</span>
                                    </RenderIf>
                                </div>
                            ]
                        } else {
                            return (
                                <div key={`doc-key-${i}`}>
                                    <FileDrop
                                        labelText={
                                            !!doc.text
                                                ? doc.text[userSettings.LanguagePreference]
                                                : typeof doc.docData[0]?.DocumentType === 'string'
                                                ? doc.docData[0].DocumentType
                                                : 'Miscellaneous or Generated Document'
                                        }
                                        classes={['parent-record-doc']}
                                        uploading={saving.has(doc.id)}
                                        disabled={doingAjax}
                                        downloadOnly={Number.isInteger(doc?.id) ? false : true}
                                        key={`file-drop-${
                                            doc.docData[0] !== undefined ? doc.docData[0].FileID : Math.floor(Math.random() * 100)
                                        }`}
                                        files={_files}
                                        drop={(event) => handleDrop(event, doc)}
                                        required={doc?.minResponseRequired > 0 ? true : false}
                                        dragover={handleDrag}
                                        remove={handleRemove}
                                    />
                                    <RenderIf isTrue={_files.length < 1}>
                                        <span>{t('File.AcceptedTypes', { acceptedFileTypes: doc.acceptedFileTypes })}</span>
                                    </RenderIf>
                                </div>
                            );
                        }

                    });

                    return _jsxParents;
                } else {
                    return shimmerJSX;
                }
            default:
                console.error(`Invalid number of arguments passed: ${arguments}`);
        }
    }

    function osdeJSXGenerator() {
        switch (arguments.length) {
            case 0:
                if (record) {
                    return (
                        <>
                            <div className="osde-question-container">
                                <ReactMarkdown className="question-text">{record.osde.nationality.text['en-US']}</ReactMarkdown>
                                <div>
                                    {record.osde.nationality.options.map((_opt) => {
                                        return (
                                            <div
                                                className="osde-options-container"
                                                key={`nationality-option-${_opt.id}`}
                                            >
                                                <label className="options">
                                                    <input
                                                        type="checkbox"
                                                        id={`nationality-${_opt.id}`}
                                                        defaultChecked={record.osde.nationality.chosenOptions.includes(_opt.id)}
                                                        value={_opt.id}
                                                        onClick={handleCheckbox}
                                                    />
                                                    <ReactMarkdown className="question-option">{_opt.text['en-US']}</ReactMarkdown>
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="osde-question-container">
                                <ReactMarkdown className="question-text">{record.osde.eds.text['en-US']}</ReactMarkdown>
                                <div>
                                    {record.osde.eds.options.map((_opt) => {
                                        return (
                                            <div
                                                className="osde-options-container"
                                                key={`eds-option-${_opt.id}`}
                                            >
                                                <label className="options">
                                                    <input
                                                        type="radio"
                                                        id={`eds-${_opt.id}`}
                                                        defaultChecked={_opt.id === record.osde.eds.chosenOptions[0]}
                                                        name="eds"
                                                        value={_opt.id}
                                                        onClick={() => {
                                                            handleOption('osde', 'eds', _opt.id);
                                                        }}
                                                    />
                                                    <ReactMarkdown className="question-option">{_opt.text['en-US']}</ReactMarkdown>
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    );
                } else {
                    return shimmerJSX;
                }
            default:
                console.error(`Invalid number of arguments: ${arguments}`);
        }
    }

    let sectionC = useMemo(() => {
        function optionJSXGenerator() {
            switch (arguments.length) {
                case 2:
                    if (record) {
                        var _question = record[arguments[0]][arguments[1]];
                        var _jsx = _question.options.map((e) => {
                            function _handleCheckbox(e) {
                                let _checked = new Set(record.sectionC.eligibility.chosenOptions);

                                if (e?.target?.checked === true) _checked.add(parseInt(e.target.value));
                                else _checked.delete(parseInt(e.target.value));

                                setRecord((prev) => {
                                    prev.sectionC.eligibility.chosenOptions = Array.from(_checked);
                                    return prev;
                                });
                            }

                            return (
                                <div
                                    key={`${arguments[1]}-key-${e.id}-${Math.floor(Math.random(render) * 100)}`}
                                    data-ident={e.id}
                                    className={editable ? ['verify', 'options'].join(' ') : 'verify'}
                                >
                                    {!editable ? (
                                        <>
                                            <span className="check-mark">{blueCheckJSX}</span>
                                            <span className={`${arguments[1]}-text`}>
                                                <ReactMarkdown>{e.text['en-US']}</ReactMarkdown>
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <input
                                                type={_question.questionType === 3 ? 'radio' : 'checkbox'}
                                                id={`${arguments[1]}-${e.id}`}
                                                defaultChecked={e.id === _question.chosenOptions[0]}
                                                name={arguments[1]}
                                                value={e.id}
                                                onClick={
                                                    _question.questionType === 3
                                                        ? () => handleOption(arguments[0], arguments[1], e.id)
                                                        : _handleCheckbox
                                                }
                                            />
                                            <label className={`${arguments[1]}-text`}>
                                                <ReactMarkdown className="question-option">{e.text['en-US']}</ReactMarkdown>
                                            </label>
                                        </>
                                    )}
                                </div>
                            );
                        });

                        if (!editable) _jsx = _jsx.filter((e) => e.props['data-ident'] === _question.chosenOptions[0]);
                        return _jsx;
                    } else {
                        return shimmerJSX;
                    }
                default:
                    console.error(`Invalid arguments passed: ${arguments}`);
            }
        }

        if (!!record && !!record?.sectionC?.eligibility) {
            return (
                <PanelSection
                    classes={['eligibility-verification']}
                    headerText={t('Record.SectionCTitle')}
                    key="sectionC"
                >
                    {optionJSXGenerator('sectionC', 'eligibility')}
                </PanelSection>
            );
        } else if (!!record && record?.sectionC?.eligibility === null) {
            return (
                <PanelSection
                    classes={['eligibility-verification']}
                    headerText={t('Record.SectionCTitle')}
                    key="sectionC"
                >
                    No eligibility questions.
                </PanelSection>
            );
        } else {
            return (
                <PanelSection
                    classes={['eligibility-verification']}
                    headerText={t('Record.SectionCTitle')}
                    key="sectionC"
                >
                    {shimmerJSX}
                </PanelSection>
            );
        }
    }, [render, record, blueCheckJSX, shimmerJSX, editable]);

    let sectionD = useMemo(() => {
        function optionJSXGenerator() {
            switch (arguments.length) {
                case 2:
                    if (record) {
                        var _question = record[arguments[0]][arguments[1]];
                        var _jsx = _question.options.map((e) => {
                            function _handleCheckbox(e) {
                                let _checked = new Set(record.sectionD.enrollment.chosenOptions);

                                if (e?.target?.checked === true) _checked.add(parseInt(e.target.value));
                                else _checked.delete(parseInt(e.target.value));

                                setRecord((prev) => {
                                    prev.sectionD.enrollment.chosenOptions = Array.from(_checked);
                                    return prev;
                                });
                            }

                            return (
                                <div
                                    key={`${arguments[1]}-key-${e.id}-${Math.floor(Math.random(render) * 100)}`}
                                    data-ident={e.id}
                                    className={editable ? ['verify', 'options'].join(' ') : 'verify'}
                                >
                                    {!editable ? (
                                        <>
                                            <span className="check-mark">{blueCheckJSX}</span>
                                            <span className={`${arguments[1]}-text`}>
                                                <ReactMarkdown>{e.text['en-US']}</ReactMarkdown>
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <input
                                                type={_question.questionType === 3 ? 'radio' : 'checkbox'}
                                                id={`${arguments[1]}-${e.id}`}
                                                defaultChecked={e.id === _question.chosenOptions[0]}
                                                name={arguments[1]}
                                                value={e.id}
                                                onClick={
                                                    _question.questionType === 3
                                                        ? () => handleOption(arguments[0], arguments[1], e.id)
                                                        : _handleCheckbox
                                                }
                                            />
                                            <label className={`${arguments[1]}-text`}>
                                                <ReactMarkdown className="question-option">{e.text['en-US']}</ReactMarkdown>
                                            </label>
                                        </>
                                    )}
                                </div>
                            );
                        });

                        if (!editable) _jsx = _jsx.filter((e) => e.props['data-ident'] === _question.chosenOptions[0]);
                        return _jsx;
                    } else {
                        return shimmerJSX;
                    }
                default:
                    console.error(`Invalid arguments passed: ${arguments}`);
            }
        }

        if (!!record && !!record?.sectionD?.enrollment) {
            return (
                <PanelSection
                    classes={['enrollment-verification']}
                    headerText={t('Record.SectionDTitle')}
                    key="sectionD"
                >
                    {optionJSXGenerator('sectionD', 'enrollment')}
                </PanelSection>
            );
        } else if (!!record && record?.sectionD?.enrollment === null) {
            return (
                <PanelSection
                    classes={['enrollment-verification']}
                    headerText={''}
                    key="sectionD"
                >
                </PanelSection>
            );
        } else {
            return (
                <PanelSection
                    classes={['enrollment-verification']}
                    headerText={t('Record.SectionDTitle')}
                    key="sectionD"
                >
                    {shimmerJSX}
                </PanelSection>
            );
        }
    }, [render, record, blueCheckJSX, shimmerJSX, editable]);

    return (
        <View
            viewTitle={recordTitleJSX}
            doingAjax={doingAjax}
            className="record-view"
            name="record"
        >
            <Form
                ajaxErrorMessage={ajaxErrorMessage}
                onValidSave={handleValidSave}
                refs={formRefs.current}
                onInvalidSave={() => {
                    setDoingAjax(false);
                }}
                primaryButton={editable}
                primaryButtonText={t('UserSettings.SaveButtonText')}
                primaryButtonInteractiveText={t('General.InteractiveSave')}
                otherButtons={
                    <ShipyardMenuButton
                        disabled={doingAjax}
                        label="Select Action"
                        actions={actions}
                        icon={<FontAwesomeIcon icon={faAngleDown} />}
                    />
                }
                onUserInteract={{ handleSave: handleSave }}
            >
                <button
                    className="back-button button-link"
                    onClick={handleBack}
                >
                    <FontAwesomeIcon icon={faArrowLeft} /> {t('Record.BackButtonLabel')}
                </button>
                {record?.statusRow?.status === 5 && review ? messageJSX : null}
                {userSettings.UserType === 3 && (
                    <RecordChanges
                        markResolved={async () => {
                            setDoingAjax(true);
                            await markResolved();
                            setDoingAjax(false);
                        }}
                        changes={changes}
                    />
                )}
                {key && record?.statusRow?.status ? (
                    <ScholarshipData
                        appInstId={key}
                        status={record?.statusRow?.status}
                        open={scholarshipDataOpen}
                        handleClose={() => setScholarshipDataOpen(false)}
                    />
                ) : null}
                {userSettings?.UserType >= 2 ?
                    <ChangeApplicationStatus
                        open={recordState?.isChangeStatusOpen || false}
                        handleClose={() => handleChangeStatus(false)}
                        handleSuccess={() => {
                            setReview(false);
                            getRecordChanges(recordState?.termId);
                            setRecord(null);
                        }}
                        StatusComponent={() => <Status status={record?.statusRow?.status} />}
                        currentStatus={record?.statusRow?.status}
                        appId={key}
                        userSettings={userSettings}
                    />
                : null}
                <DeleteApplication
                    open={showDelete}
                    appInfo={{
                        'Unique ID': record?.uid,
                        Student: record?.name,
                        SY: record?.termDescription,
                        Type: record?.appInfo?.find((info) => info.QuestionID === 104).AnswerText,
                    }}
                    appInstId={key}
                    handleClose={() => setShowDelete(false)}
                    handleDelete={handleBack}
                />

                <Panel>
                    <PanelSection
                        classes={['application-info']}
                        headerText="Application Information"
                    >
                        <div className="application-info-grid">
                            <div>
                                <div>
                                    <div className="label-div">{t('Record.StatusLabel')}</div>
                                    {typeof record?.statusRow?.status === 'number' ? (
                                        <div>
                                            <Status status={record.statusRow.status} />
                                        </div>
                                    ) : (
                                        <div>{shimmerJSX}</div>
                                    )}
                                </div>
                                <div>
                                    <div className="label-div">{'Application Type'}:</div>
                                    <div>
                                        {record?.appInfo?.find((info) => info.QuestionID === 104)
                                            ? record?.appInfo?.find((info) => info.QuestionID === 104).AnswerText
                                            : shimmerJSX}
                                    </div>
                                </div>
                                <div>
                                    <div className="label-div">{t('Record.SubmittedByLabel')}:</div>
                                    <div>
                                        {record?.appInfo?.find((info) => info.QuestionID === 105)
                                            ? record?.appInfo?.find((info) => info.QuestionID === 105).AnswerText
                                            : shimmerJSX}
                                    </div>
                                </div>
                            </div>
                            {userSettings.UserType >= 3 ? (
                                <div>
                                    <div>
                                        <div className="label-div">{t('Record.SubmittedOnLabel')}:</div>
                                        <div>
                                            {record?.appInfo?.find((info) => info.QuestionID === 106) ? (
                                                record.appInfo.find((info) => info.QuestionID === 106).AnswerDate !== null ? (
                                                    <DateTimeText
                                                        date={record?.appInfo?.find((info) => info.QuestionID === 106).AnswerDate}
                                                    />
                                                ) : (
                                                    'N/A'
                                                )
                                            ) : (
                                                shimmerJSX
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="label-div">{'10 Day Timeline Expiration'}:</div>
                                        <RenderIf isTrue={record?.appInfo?.find((info) => info.QuestionID === 107)}>
                                            <TenDayTimelineEdit
                                                date={record?.appInfo?.find((info) => info?.QuestionID === 107)?.AnswerDate}
                                                appInstId={key}
                                                questionId={107}
                                                onSave={(newDate) =>
                                                    setRecord((prev) => {
                                                        const question = record?.appInfo?.find((info) => info?.QuestionID === 107);
                                                        question.AnswerDate = newDate;

                                                        let newState = {
                                                            ...prev,
                                                            appInfo: [...prev.appInfo, question],
                                                        };

                                                        return newState;
                                                    })
                                                }
                                            />
                                        </RenderIf>
                                    </div>
                                    <div>
                                        <div className="label-div">{'Date Approved'}:</div>
                                        <div>
                                            {record?.appInfo?.find((info) => info.QuestionID === 108) ? (
                                                record.appInfo.find((info) => info.QuestionID === 108).AnswerDate !== null ? (
                                                    <DateTimeText
                                                        date={record?.appInfo?.find((info) => info.QuestionID === 108).AnswerDate}
                                                    />
                                                ) : (
                                                    'N/A'
                                                )
                                            ) : (
                                                shimmerJSX
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </PanelSection>
                    <PanelSection
                        classes={['student-info']}
                        headerText={t('Record.SectionATitle')}
                    >
                        <div className="student-info-grid">
                            <div>
                                <span>
                                    {recordDisplayCheck(record?.sectionA?.studentFirstName)
                                        ? inputJSXGenerator(t('Record.FirstNameLabel'), 'sectionA', 'studentFirstName')
                                        : shimmerJSX}
                                </span>
                                <span>
                                    {recordDisplayCheck(record?.sectionA?.studentLastName)
                                        ? inputJSXGenerator(t('Record.LastNameLabel'), 'sectionA', 'studentLastName')
                                        : shimmerJSX}
                                </span>
                                <span>
                                    {recordDisplayCheck(record?.sectionA?.dob)
                                        ? inputJSXGenerator(t('Record.DOBLabel'), 'sectionA', 'dob')
                                        : shimmerJSX}
                                </span>
                                <span>
                                    {recordDisplayCheck(record?.sectionA?.nextGrade)
                                        ? inputJSXGenerator(t('Record.GradeLabel'), 'sectionA', 'nextGrade')
                                        : shimmerJSX}
                                </span>
                                <span>{record !== null ? inputJSXGenerator(t('Record.STNLabel'), 'stn') : shimmerJSX}</span>
                                <RenderIf isTrue={userSettings?.UserType >= 3}>
                                    <span>
                                            {recordDisplayCheck(record?.sectionA?.districtIEP)
                                                ? inputJSXGenerator(t('Record.SchoolDistrictIEPLabel'), 'sectionA', 'districtIEP')
                                                : shimmerJSX}
                                    </span>
                                </RenderIf>
                                {userSettings?.UserType >= 3 ? (
                                    <span>
                                        {recordDisplayCheck(record?.sectionA?.IEPorISP)
                                            ? inputJSXGenerator(t('Record.IEPISPLabel'), 'sectionA', 'IEPorISP')
                                            : shimmerJSX}
                                    </span>
                                ) : null}
                            </div>
                            {userSettings.UserType >= 3 ? (
                                <div>
                                    <span>
                                        {Number.isInteger(record?.sectionA?.primaryDisability)
                                            ? inputJSXGenerator(t('Record.PrimaryDisabilityLabel'), 'sectionA', 'primaryDisability')
                                            : shimmerJSX}
                                    </span>
                                    <span>
                                        {Number.isInteger(record?.sectionA?.secondaryDisability)
                                            ? inputJSXGenerator(t('Record.SecondaryDisabilityLabel'), 'sectionA', 'secondaryDisability')
                                            : shimmerJSX}
                                    </span>
                                    <span>
                                        {recordDisplayCheck(record?.sectionA?.school)
                                            ? inputJSXGenerator(t('Record.SchoolNameLabel'), 'sectionA', 'school')
                                            : shimmerJSX}
                                    </span>
                                    <span>
                                        {recordDisplayCheck(record?.sectionA?.acceptanceDate)
                                            ? inputJSXGenerator(t('Record.AcceptanceDateLabel'), 'sectionA', 'acceptanceDate')
                                            : shimmerJSX}
                                    </span>
                                        {recordDisplayCheck(record?.sectionA?.fullOrPart)
                                            ? <span>{inputJSXGenerator(t('Record.FullPartTimeLabel'), 'sectionA', 'fullOrPart')}</span>
                                            : shimmerJSX}
                                    <span>
                                        {recordDisplayCheck(record?.sectionA?.revocationDate)
                                            ? inputJSXGenerator(t('Record.RevocationDateLabel'), 'sectionA', 'revocationDate')
                                            : shimmerJSX}
                                    </span>
                                    <span>
                                        {record !== null
                                            ? inputJSXGenerator(t('Record.InitialYearLabel'), 'sectionA', 'initYear')
                                            : shimmerJSX}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                        {record?.appType === 2 ? (
                            <>
                                <span>
                                    {recordDisplayCheck(record?.sectionA?.renewalQuestion1)
                                        ? inputJSXGenerator(null, 'sectionA', 'renewalQuestion1')
                                        : shimmerJSX}
                                </span>
                                <span>
                                    {recordDisplayCheck(record?.sectionA?.renewalQuestion2)
                                        ? inputJSXGenerator(null, 'sectionA', 'renewalQuestion2')
                                        : shimmerJSX}
                                </span>
                            </>
                        ) : null}
                    </PanelSection>
                    <PanelSection
                        classes={['parent-info']}
                        headerText={t('Record.SectionBTitle')}
                    >
                        <div className="parent-info-grid">
                            <div>
                                <span>
                                    {recordDisplayCheck(record?.sectionB?.parentFirstName)
                                        ? inputJSXGenerator(t('Record.ParentFirstNameLabel'), 'sectionB', 'parentFirstName')
                                        : shimmerJSX}
                                </span>
                                <span>
                                    {recordDisplayCheck(record?.sectionB?.parentLastName)
                                        ? inputJSXGenerator(t('Record.LastNameLabel'), 'sectionB', 'parentLastName')
                                        : shimmerJSX}
                                </span>
                                {userSettings.UserType >= 3 ? (
                                    <span>
                                        {recordDisplayCheck(record?.sectionB?.tin)
                                            ? inputJSXGenerator(t('Record.SSNLabel'), 'sectionB', 'tin')
                                            : shimmerJSX}
                                    </span>
                                ) : null}
                                <span>
                                    {recordDisplayCheck(record?.sectionB?.address)
                                        ? inputJSXGenerator(t('Record.ParentAddressLabel'), 'sectionB', 'address')
                                        : shimmerJSX}
                                </span>
                                <span>
                                    {recordDisplayCheck(record?.sectionB?.city)
                                        ? inputJSXGenerator(t('Record.CityLabel'), 'sectionB', 'city')
                                        : shimmerJSX}
                                </span>
                                <span>
                                    {recordDisplayCheck(record?.sectionB?.state)
                                        ? inputJSXGenerator(t('Record.StateLabel'), 'sectionB', 'state')
                                        : shimmerJSX}
                                </span>
                                <span>
                                    {recordDisplayCheck(record?.sectionB?.zip)
                                        ? inputJSXGenerator(t('Record.ZipLabel'), 'sectionB', 'zip')
                                        : shimmerJSX}
                                </span>
                            </div>
                            <div>
                                <span>
                                    {recordDisplayCheck(record?.sectionB?.phoneNumber)
                                        ? inputJSXGenerator(t('Record.PhoneLabel'), 'sectionB', 'phoneNumber')
                                        : shimmerJSX}
                                </span>
                                <span>
                                    {recordDisplayCheck(record?.sectionB?.email)
                                        ? inputJSXGenerator(t('Record.EmailLabel'), 'sectionB', 'email')
                                        : shimmerJSX}
                                </span>

                                {userSettings.UserType >= 3 ? (
                                    <>
                                        <span>
                                            {recordDisplayCheck(record?.sectionB?.parentFirstName)
                                                ? inputJSXGenerator(t('Record.PayeeNameLabel'), 'sectionB', 'payeeName')
                                                : shimmerJSX}
                                        </span>
                                        <span>
                                            {recordDisplayCheck(record?.sectionB?.payeeNum)
                                                ? inputJSXGenerator(t('Record.PayeeNumberLabel'), 'sectionB', 'payeeNum')
                                                : shimmerJSX}
                                        </span>
                                        <span>
                                            {recordDisplayCheck(record?.sectionB?.location)
                                                ? inputJSXGenerator(t('Record.LocationLabel'), 'sectionB', 'location')
                                                : shimmerJSX}
                                        </span>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </PanelSection>

                    {editable ? sectionC : null}
                    {editable ? sectionD : null}
                    {/* TODO: Re-introduce this functionality once Requirements are defined post 1.0.0 */}
                    {/* {record?.statusRow?.status === 3 ?
											<PanelSection classes={["school-verification"]} headerText={t("Record.SectionETitle")}>
													<span className="blue-check">
															<input type="checkbox" className="school-checkbox" />
													</span>
													<span className="school-text">{t("Record.SchoolText1") + " " + record?.sectionA?.school + " " + t("Record.SchoolText2") + " " + schoolYear + " " + t("Record.SchoolText3")}</span>
											</PanelSection>
									:
											null
									} */}
                    {/* {editable ?
											<PanelSection classes={["documents"]} headerText="Documents">
													{documentsJSXGenerator()}
											</PanelSection>
									:
											null
									} */}
                    <PanelSection
                        classes={['documents']}
                        headerText={t('Record.DocumentsTitle')}
                    >
                        {documentsJSXGenerator()}
                    </PanelSection>
                    {editable ? (
                        <PanelSection
                            classes={['osde']}
                            headerText={t('Record.DataTitle')}
                        >
                            {osdeJSXGenerator()}
                        </PanelSection>
                    ) : null}
                </Panel>
            </Form>
        </View>
    );
}
